import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {RootDesc} from "@ui/descriptions/RootDesc";
import {EditField} from "@ui/forms/EditField";
import {ViewField} from "@ui/forms/ViewField";
import {RadioGroup} from "@ui/inputs/RootRadio";
import {RootIcon} from "@ui/icons/RootIcon";
import {
  GridLayout,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import {useTranslation} from "react-i18next";
import "react-resizable/css/styles.css";

import styles from "../styles.module.scss";
import {EvaluationToolInfo} from "./EvaluationToolInfo";
import {
  ADD_SLOT_ID,
  DEFAULT_CRITERION_WEIGHT,
  GRID_COLS,
  GRID_MARGIN_Y,
  GRID_ROW_HEIGHT,
  appendSubcriterion,
  applyLayoutChange,
  applyRowPositions,
  getEvaluationToolLabel,
  getEvaluationToolOptions,
  filterCardLayout,
  getAddSlotLayoutItem,
  isSameLayout,
  pixelsToGridH,
  reorderSubcriteriaByLayout,
  sortLayoutItems,
} from "./criteriaGridHelpers";

const SubcriterionWeightField = ({item, readOnly, onChange, t}) => (
  <li className={styles.gridItemWeight}>
    <div className={styles.gridItemWeightLabel}>
      <RootDesc>
        <b>{t("input.subcriterion_weights")}</b>
      </RootDesc>
    </div>
    <RadioGroup
      rating
      ratingType={"bullet"}
      required={false}
      name={`subcriterion-weight-${item.id}`}
      value={item.weight ?? DEFAULT_CRITERION_WEIGHT}
      onChange={(ratingValue) => onChange?.(item.id, "weight", ratingValue)}
      disabled={readOnly}
    />
  </li>
);

const SubcriterionCard = ({
  item,
  readOnly,
  onChange,
  onRemove,
  canRemove,
  measureRef,
  evaluationToolOptions,
  t,
}) => {
  const updateField = (field, value) => {
    onChange(item.id, field, value);
  };

  if (readOnly) {
    return (
      <div
        ref={measureRef}
        className={styles.gridItem}
      >
        <ul className={styles.gridItemFields}>
          <ViewField
            label={t("input.subcriterion_description")}
            value={item.description || "-"}
            direction={"col"}
          />
          <ViewField
            label={t("input.subcriterion_parameter")}
            value={item.parameter || "-"}
            direction={"col"}
          />
          <ViewField
            label={t("input.subcriterion_tool")}
            direction={"col"}
            customContent={
              <div className={styles.evaluationToolValue}>
                <RootDesc>
                  <span
                    className={"block p-[4rem_8rem]"}
                    dangerouslySetInnerHTML={{
                      __html: getEvaluationToolLabel(item.tool, t),
                    }}
                  />
                </RootDesc>
                <EvaluationToolInfo tool={item.tool} />
              </div>
            }
          />
          <SubcriterionWeightField
            item={item}
            readOnly
            t={t}
          />
        </ul>
      </div>
    );
  }

  return (
    <div
      ref={measureRef}
      className={styles.gridItem}
    >
      <div className={styles.gridItemHeader}>
        <span className={`${styles.gridItemDragHandle} gridItemDragHandle`}>
          {Array.from({length: 6}, (_, index) => (
            <span
              key={index}
              className={styles.gridItemDragDot}
            />
          ))}
        </span>

        {canRemove && (
          <button
            type={"button"}
            className={styles.gridItemRemove}
            onClick={() => onRemove(item.id)}
            aria-label={t("table.button.delete")}
          >
            <RootIcon
              id={"delete"}
              fill={"var(--red)"}
              width={16}
              height={16}
            />
          </button>
        )}
      </div>

      <ul className={styles.gridItemFields}>
        <EditField
          direction={"col"}
          label={t("input.subcriterion_description")}
          type={"textarea"}
          value={item.description}
          onChange={(e) => updateField("description", e.target.value)}
        />

        <EditField
          direction={"col"}
          label={t("input.subcriterion_parameter")}
          type={"textarea"}
          value={item.parameter}
          onChange={(e) => updateField("parameter", e.target.value)}
        />

        <EditField
          direction={"col"}
          label={t("input.subcriterion_tool")}
          type={"select"}
          height={280}
          options={evaluationToolOptions}
          activeOption={item.tool || ""}
          emptyResult={t("input.edit_select_empty")}
          onSelect={(selectedOption) =>
            updateField(
              "tool",
              selectedOption?.value ?? selectedOption?.name ?? "",
            )
          }
        />

        <SubcriterionWeightField
          item={item}
          onChange={onChange}
          t={t}
        />
      </ul>
    </div>
  );
};

const AddSubcriterionSlot = ({onClick}) => (
  <button
    type={"button"}
    className={styles.gridAddSlot}
    onClick={onClick}
  >
    +
  </button>
);

export const CriteriaSectionBlock = ({
  title,
  layout,
  subcriteria,
  readOnly = false,
  onTitleChange,
  onGridReorder,
  onSubcriterionChange,
  onAddSubcriterion,
  onRemoveSubcriterion,
}) => {
  const {t} = useTranslation();
  const evaluationToolOptions = useMemo(() => getEvaluationToolOptions(t), [t]);
  const {width, containerRef, mounted} = useContainerWidth();
  const [measuredGridH, setMeasuredGridH] = useState({});
  const measureObserversRef = useRef({});
  const measureCallbacksRef = useRef(new Map());

  const orderedSubcriteria = useMemo(
    () => reorderSubcriteriaByLayout(subcriteria, layout),
    [subcriteria, layout],
  );

  const sizedLayout = useMemo(() => {
    const cards = sortLayoutItems(filterCardLayout(layout)).map((item) => ({
      ...item,
      h: measuredGridH[item.i] ?? item.h,
    }));

    return applyRowPositions(cards);
  }, [layout, measuredGridH]);

  const displayLayout = useMemo(() => {
    if (readOnly) {
      return sizedLayout;
    }

    return [
      ...sizedLayout,
      getAddSlotLayoutItem(subcriteria.length, sizedLayout),
    ];
  }, [sizedLayout, subcriteria.length, readOnly]);

  useEffect(() => {
    const activeIds = new Set(subcriteria.map((item) => item.id));

    Object.entries(measureObserversRef.current).forEach(([id, observer]) => {
      if (!activeIds.has(id)) {
        observer.disconnect();
        delete measureObserversRef.current[id];
        measureCallbacksRef.current.delete(id);
      }
    });

    setMeasuredGridH((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => activeIds.has(id)),
      );

      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [subcriteria]);

  useEffect(() => {
    return () => {
      Object.values(measureObserversRef.current).forEach((observer) => {
        observer.disconnect();
      });
      measureObserversRef.current = {};
      measureCallbacksRef.current.clear();
    };
  }, []);

  const getMeasureRef = useCallback((id) => {
    if (!measureCallbacksRef.current.has(id)) {
      measureCallbacksRef.current.set(id, (node) => {
        measureObserversRef.current[id]?.disconnect();
        delete measureObserversRef.current[id];

        if (!node) {
          return;
        }

        const reportHeight = () => {
          const gridH = pixelsToGridH(node.scrollHeight);

          setMeasuredGridH((prev) =>
            prev[id] === gridH ? prev : {...prev, [id]: gridH},
          );
        };

        const observer = new ResizeObserver(() => {
          reportHeight();
        });

        observer.observe(node);
        measureObserversRef.current[id] = observer;
        requestAnimationFrame(reportHeight);
      });
    }

    return measureCallbacksRef.current.get(id);
  }, []);

  const syncLayout = useCallback(
    (nextLayout) => {
      const merged = filterCardLayout(nextLayout).map((item) => ({
        ...item,
        h: measuredGridH[item.i] ?? item.h,
      }));
      const next = applyLayoutChange(subcriteria, merged);
      const sameOrder = subcriteria.every(
        (item, index) => item.id === next.subcriteria[index]?.id,
      );

      if (isSameLayout(layout, next.layout) && sameOrder) {
        return;
      }

      onGridReorder?.(next);
    },
    [subcriteria, layout, measuredGridH, onGridReorder],
  );

  const handleAdd = () => {
    const {subcriteria: nextSubcriteria, layout: nextLayout} =
      appendSubcriterion(subcriteria, sizedLayout);

    onAddSubcriterion?.(nextSubcriteria, nextLayout);
  };

  return (
    <div className={styles.criteriaSection}>
      <ul className={styles.criteriaTitleField}>
        {readOnly ? (
          <ViewField
            label={t("input.criteria_title")}
            value={title || "-"}
          />
        ) : (
          <EditField
            label={t("input.criteria_title")}
            value={title}
            onChange={(e) => onTitleChange?.(e.target.value)}
          />
        )}
      </ul>

      <div className={styles.criteriaSectionRow}>
        <div className={styles.criteriaSectionLabel}>
          <RootDesc>
            <b
              dangerouslySetInnerHTML={{
                __html: `${t("input.weights")}`,
              }}
            />
          </RootDesc>
        </div>

        <div
          ref={containerRef}
          className={styles.gridContainer}
        >
          {mounted && width > 0 && (
            <GridLayout
              width={width}
              layout={displayLayout}
              onLayoutChange={readOnly ? undefined : syncLayout}
              onDragStop={readOnly ? undefined : syncLayout}
              compactor={verticalCompactor}
              gridConfig={{
                cols: GRID_COLS,
                rowHeight: GRID_ROW_HEIGHT,
                margin: [12, GRID_MARGIN_Y],
              }}
              dragConfig={{
                enabled: !readOnly,
                handle: ".gridItemDragHandle",
                cancel: `.${styles.gridAddSlot}`,
              }}
              resizeConfig={{enabled: false}}
            >
              {orderedSubcriteria.map((item) => (
                <div key={item.id}>
                  <SubcriterionCard
                    item={item}
                    readOnly={readOnly}
                    onChange={onSubcriterionChange}
                    onRemove={onRemoveSubcriterion}
                    canRemove={!readOnly && subcriteria.length > 1}
                    measureRef={getMeasureRef(item.id)}
                    evaluationToolOptions={evaluationToolOptions}
                    t={t}
                  />
                </div>
              ))}

              {!readOnly && (
                <div key={ADD_SLOT_ID}>
                  <AddSubcriterionSlot onClick={handleAdd} />
                </div>
              )}
            </GridLayout>
          )}
        </div>
      </div>
    </div>
  );
};
